import { Formik, useFormikContext } from "formik";
import { useEffect } from "react";
import apiRequest from "../../../utils/admin/apiRequest";
import InputField from "../../ui/form/InputField";
import Button from "../../ui/utils/Button";
import SelectField from "../../ui/form/SelectField";
import usePost from "../../../hooks/api/usePost";
import Alert from "../../ui/utils/Alert";
import useFetch from "../../../hooks/api/useFetch";
import { createCorporateCodeValidationSchema } from "../../../validation/admin/corporateCodeValidation";

const LocationWatcher = ({
  fetchStates,
  fetchCities,
  setBlankResponse,
  setCityBlankResponse,
}) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });

      if (!values.stateId) {
        setFieldValue("stateId", "");
      }
    } else {
      setBlankResponse();
    }
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });

      if (!values.cityId) {
        setFieldValue("cityId", "");
      }
    } else {
      setCityBlankResponse();
    }
  }, [values.stateId]);

  return null;
};

/* ---------- Main Component ---------- */
const CreateCorporateCode = ({ closeModal, callback }) => {
  const { error, isLoading, post, serverErrors } = usePost(
    apiRequest.createCorporateCode
  );

  const initialValues = {
    corporateName: "",
    corporateCode: "",
    buildingName: "",
    pincode: "",
    countryId: "6937b28dda25e4d40acc2983",
    stateId: "6937b35cda25e4d40acc4ce4",
    cityId: "",
  };

  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    autoFetch: false,
  });



  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Create Corporate Code</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        validationSchema={createCorporateCodeValidationSchema}
        onSubmit={async (values, { resetForm }) => {
          const response = await post({ data: { body: values } });
          if (response?.response) {
            callback?.();
            resetForm();
            closeModal();
          }
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, touched, errors }) => (
          <>
            <LocationWatcher
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />

            <form onSubmit={handleSubmit} className="text-sm">
              {error && <Alert message={error} type="error" />}

              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                <InputField
                  name="corporateName"
                  label="Corporate Name"
                  value={values.corporateName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.corporateName && errors.corporateName}
                  serverError={serverErrors?.corporateName}
                />
                <InputField
                  name="buildingName"
                  label="Building Name"
                  value={values.buildingName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.buildingName && errors.buildingName}
                  serverError={serverErrors?.buildingName}
                />

                <InputField
                  name="corporateCode"
                  label="Corporate Code"
                  value={values.corporateCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.corporateCode && errors.corporateCode}
                  serverError={serverErrors?.corporateCode}
                />

                <InputField
                  name="pincode"
                  label="Pincode"
                  value={values.pincode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.pincode && errors.pincode}
                  serverError={serverErrors?.pincode}
                />

                <SelectField
                  name="countryId"
                  label="Country"
                  value={values.countryId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.countryId && errors.countryId}
                  options={countryData?.countries?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                />

                <SelectField
                  name="stateId"
                  label="State"
                  value={values.stateId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.stateId && errors.stateId}
                  options={stateData?.states?.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                />

                <SelectField
                  name="cityId"
                  label="City"
                  value={values.cityId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.cityId && errors.cityId}
                  options={cityData?.cities?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                />

              </div>

              <div className="border-b" />

              <div className="flex justify-end gap-2 p-4">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={isLoading}>
                  Create
                </Button>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateCorporateCode;
